import React from 'react';
import { withBlockExtensions } from '@plone/volto/helpers';
import {
  MPFBlockCardHeader,
  MPFLink,
} from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from 'volto-ministerio-publico-federal/components/View/CapaView/EditItemButton';
import { StarIcon } from 'volto-ministerio-publico-federal/components/View/CapaView/StarIcon';

import {
  SUBHOME_DESTAQUE_MAX_ITEMS,
  SUBHOME_DESTAQUE_TITLE,
  subHomeDestaqueLinkIsComplete,
  toSubHomeDestaqueLinks,
  type SubHomeDestaqueData,
  type SubHomeDestaqueLink,
} from './helpers';

interface ViewProps {
  data: SubHomeDestaqueData;
  block: string;
  isEditMode?: boolean;
  /** Presente só no editor: abre o atalho de índice N na barra lateral. */
  onEditItem?: (index: number) => void;
}

/* Pílula do design: `primary/PrimaryContainer` (#F3F2F7) sobre
   `primary/OnPrimaryContainer` (#09024E). Nenhuma variante do `MPFButton`
   cobre esse par — as dele são `primary` e `secondary` sólidos —, então o
   estilo é bespoke aqui, como o `AcoesRapidasCard` da capa também faz. */
const PILL_CLASS =
  'flex min-h-[3.5rem] w-full items-center justify-center rounded-lg bg-primary-container px-4 py-4 text-center text-base font-normal text-on-primary-container transition-colors hover:bg-on-primary-container hover:text-on-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:bg-night-700 dark:text-night-100';

/* `not-prose` desliga a tipografia de texto corrido que a página herda do
   editor (`[class*='__editor-content']`, em `base/_editor-content.scss`):
   as faixas trazem a própria tipografia, e sem isto o `prose` recolore e
   redimensiona títulos, links e listas de dentro do cartão. Mesmo recurso
   que `mpfNoticiasPorTema` e `mpfCampanhas` já usam. */
const ROOT_CLASS = 'mpf-subhome-card not-prose';

const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mpf-subhome-card__placeholder">{children}</p>
);

/**
 * Faixa "Destaque" da sub-home (bloco `mpfSubHomeDestaque`): um punhado de
 * atalhos em pílula, sob o mesmo cabeçalho em cartão das demais faixas.
 *
 * O cabeçalho segue o padrão do card "O que você deseja fazer hoje?" da capa
 * (`View/CapaView/AcoesRapidasCard.tsx`) — estrela e régua na cor de destaque
 * —, mas o conteúdo é outro: lá é uma lista vertical de serviços com seta,
 * aqui é uma linha de botões.
 */
export const MpfSubHomeDestaqueBlockView: React.FC<ViewProps> = ({
  data,
  block,
  isEditMode,
  onEditItem,
}) => {
  const titleId = `subhome-destaque-${block}`;
  const links = React.useMemo(
    () => toSubHomeDestaqueLinks(data?.items),
    [data?.items],
  );
  // No editor o atalho pela metade continua na tela, senão o lápis dele
  // sumiria justo enquanto está sendo preenchido.
  const visible = isEditMode
    ? links
    : links.filter(subHomeDestaqueLinkIsComplete);
  const overflowCount = (data?.items?.length ?? 0) - SUBHOME_DESTAQUE_MAX_ITEMS;

  if (visible.length === 0) {
    return isEditMode ? (
      <section className={ROOT_CLASS}>
        <Header data={data} titleId={titleId} />
        <Notice>Escolha os atalhos da faixa na barra lateral.</Notice>
      </section>
    ) : null;
  }

  return (
    <section aria-labelledby={titleId} className={ROOT_CLASS}>
      <Header data={data} titleId={titleId} />

      {/* Uma coluna no mobile, duas no tablet e três no desktop, como o
          design. `grid` (e não `flex-1` por item) para as pílulas terem todas
          a mesma largura mesmo com rótulos de tamanhos bem diferentes.

          `flex` no `<li>` é o que apaga o marcador de lista: o `not-prose` do
          cartão NÃO alcança a regra que o desenha, porque ela é própria do
          projeto (`[class*='__editor-content'] ul li:not(…)`, em
          `base/_editor-content.scss`) e foi escrita com especificidade real —
          o `not-prose` só neutraliza os seletores `:where()` do plugin de
          tipografia. Trocar o `display` resolve sem briga de especificidade:
          marcador só existe em `display: list-item`. Mesmo par `ul`/`li` com
          `display` trocado do `AcoesRapidasCard` da capa. */}
      <ul className="grid w-full grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((link) => (
          <li key={link.key} className="relative flex min-w-0">
            <Pill link={link} isEditMode={isEditMode} />
            {onEditItem && (
              <EditItemButton
                label={`Editar atalho: ${link.label || 'sem rótulo'}`}
                onClick={() => onEditItem(link.sourceIndex)}
                className="absolute right-1 top-1"
              />
            )}
          </li>
        ))}
      </ul>

      {isEditMode && overflowCount > 0 && (
        <Notice>
          {overflowCount === 1
            ? `O último atalho não aparece: a faixa exibe no máximo ${SUBHOME_DESTAQUE_MAX_ITEMS}.`
            : `Os últimos ${overflowCount} atalhos não aparecem: a faixa exibe no máximo ${SUBHOME_DESTAQUE_MAX_ITEMS}.`}
        </Notice>
      )}
    </section>
  );
};

const Header: React.FC<{ data: SubHomeDestaqueData; titleId: string }> = ({
  data,
  titleId,
}) => (
  <MPFBlockCardHeader
    icon={
      <StarIcon className="h-6 w-6 shrink-0 text-[color:var(--yellow-400)]" />
    }
    title={data?.title || SUBHOME_DESTAQUE_TITLE}
    titleId={titleId}
    ruleClassName="bg-[color:var(--yellow-400)]"
  />
);

/**
 * A pílula em si. Um `<a>` sem `href` não é clicável nem focável e ainda
 * assim é anunciado como link — por isso o atalho incompleto (que só existe
 * no editor) sai como `<span>`. No editor, navegar seria pior ainda: tira o
 * editor da página.
 */
const Pill: React.FC<{ link: SubHomeDestaqueLink; isEditMode?: boolean }> = ({
  link,
  isEditMode,
}) => {
  const label = link.label || 'Atalho sem rótulo';

  if (!subHomeDestaqueLinkIsComplete(link) || isEditMode) {
    return <span className={PILL_CLASS}>{label}</span>;
  }

  return (
    <MPFLink
      href={link.href}
      openInNewTab={link.external || undefined}
      className={PILL_CLASS}
    >
      {label}
    </MPFLink>
  );
};

export default withBlockExtensions(MpfSubHomeDestaqueBlockView);
